import React, { FC } from "react";
import styles from "../../styles/Template.module.css";
import SelectInputBox from "../../components/Modal/SelectInputBox";
import TextAreaField from "../../components/Modal/TextAreaField";
import AnswerSection from "./AnswerSection";
import QuestionSection from "./QuestionSection";
import { FormData, Question } from "../../hooks/rtmTemplate/index.type";

type CategorySectionProps = {
  formData?: FormData;
  setFormData?: React.Dispatch<React.SetStateAction<FormData>>;
  next: boolean;
  setNext: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAnswerType: string;
  setSelectedAnswerType: React.Dispatch<React.SetStateAction<string>>;
  editingQuestion: QuestionWithErrors;
 handleToggleEditState: (state: boolean) => void;
  isEditing:boolean
  
};

export type QuestionError = {
  categoryId?: string;
  questionText?: string;
  answerType?: string;
  option?: string;
  scale?: string;
  scaleOptionErrors?: { key?: string; name?: string }[];
  hasQuestionDependOnThis?: string;
  dependableQuestionId?: string;
  dependableQuestionType?: string;
  dependableQuestionOption?: string;
  questionDependable?: string;
  optionErrors?: string[];

};
export type QuestionWithErrors = Question & {
  errors?: QuestionError;
};

const MultipleCategorySection: FC<CategorySectionProps> = ({
  formData,
  setFormData,
  editingQuestion,
  selectedAnswerType,
  setSelectedAnswerType,
  handleToggleEditState,
  isEditing
}) => {
 
  const [currentQuestion, setCurrentQuestion] =
    React.useState<QuestionWithErrors>({
      text: "",
      questionId: "",
      categoryId: "",
      sequence:0,
      answerType: "",
      option: [{ key: "", name: "", textboxRequired: "no" }],
      hasQuestionDependOnThis: "no",
      questionDependable: "no",
      dependableQuestionId: "",
      dependableQuestionOption: "",
      dependableQuestionType: "",
      errors: {},
      scale: {
        min: { key: "", name: "", textboxRequired: "no" },
        max: { key: "", name: "", textboxRequired: "no" },
      },
    });

  React.useEffect(() => {
    if (editingQuestion) {
      const updatedQuestion = { ...editingQuestion };
      if (editingQuestion.answerType === "scale") {
        updatedQuestion.scale = {
          min: {
            key: editingQuestion.scale?.min.key,
            name: editingQuestion.scale?.min.name || "",
            textboxRequired: "no",
          },
          max: {
            key: editingQuestion.scale?.max.key,
            name: editingQuestion.scale?.max.name || "",
            textboxRequired: "no",
          },
        };
      }

      setCurrentQuestion(updatedQuestion);
      setSelectedAnswerType(editingQuestion.answerType || "");
    }
  }, [editingQuestion, setSelectedAnswerType]);

  const validateQuestion = () => {
    const newErrors: typeof currentQuestion.errors = {};

    if (
      formData?.questionGroupedByCategory === "yes" &&
      !currentQuestion.categoryId
    ) {
      newErrors.categoryId = "Category name is required";
    }

    if (!currentQuestion.text) {
      newErrors.questionText = "Question is required";
    }

    if (!currentQuestion.answerType) {
      newErrors.answerType = "Answer Type is required";
    }

    if (
      currentQuestion.answerType === "single_option" ||
      currentQuestion.answerType === "multiple_option"
    ) {
      const optionErrors = currentQuestion.option.map((opt) =>
        !opt.name?.trim() ? "Option label is required" : ""
      );

      const hasEmptyOption = optionErrors.some((error) => error !== "");
      if (hasEmptyOption) {
        newErrors.optionErrors = optionErrors;
      }
    }

    if (currentQuestion.answerType === "scale") {
      const scaleErrors: [
        { key?: string; name?: string },
        { key?: string; name?: string }
      ] = [{}, {}];
      const { min, max } = currentQuestion.scale;

      // Validate min.key
      if (!min?.key?.toString().trim()) {
        scaleErrors[0].key = "Start value is required";
      }

      // Validate min.name
      if (!min?.name?.trim()) {
        scaleErrors[0].name = "Start label is required";
      }

      // Validate max.key
      if (!max?.key?.toString().trim()) {
        scaleErrors[1].key = "End value is required";
      }

      // Validate max.name
      if (!max?.name?.trim()) {
        scaleErrors[1].name = "End label is required";
      }

      const hasErrors =
        Object.keys(scaleErrors[0]).length > 0 ||
        Object.keys(scaleErrors[1]).length > 0;

      if (hasErrors) {
        newErrors.scaleOptionErrors = scaleErrors;
      }
    }

    if (currentQuestion.questionDependable === "") {
      newErrors.questionDependable = "Please select Yes or No";
    }

    if (currentQuestion.questionDependable === "yes") {
      if (!currentQuestion.dependableQuestionType) {
        newErrors.dependableQuestionType = "Answer is required";
      }
      if (!currentQuestion.dependableQuestionOption) {
        newErrors.dependableQuestionOption = "Question is required";
      }
    }

    setCurrentQuestion((prev) => ({
      ...prev,
      errors: newErrors,
    }));

    return Object.keys(newErrors).length === 0;
  };


  return (
    <div className={styles.sub_section_1}>
      {formData?.questionGroupedByCategory === "yes" && (
        <div className={styles.select_category}>
          <span>Category</span>
          <SelectInputBox
            value={currentQuestion.categoryId}
            onChange={(e) =>
              setCurrentQuestion((prev) => ({
                ...prev,
                categoryId: e.target.value,
                errors: { ...prev.errors, categoryId: "" },
              }))
            }
            containerClass={styles.category_dropdown}
            errorMessage={currentQuestion.errors?.categoryId}
            errorClass={styles.error}
            disable={isEditing}
          >
            <option value="" disabled>
              Select Category
            </option>
            {formData?.categories && formData.categories.length > 0 ? (
              formData.categories.map((category, index) => (
                <option key={index} value={category.key}>
                  {category.name}
                </option>
              ))
            ) : (
              <option value="" disabled>
                No categories available
              </option>
            )}
          </SelectInputBox>
        </div>
      )}

      <div className={styles.question_container}>
        <div className={styles.question_area}>
          <span>Question:</span>
          <TextAreaField
            className={styles.questionArea_wrapper}
            customClass={styles.question_input}
            counterClass={styles.question_word_count}
            span={true}
            value={currentQuestion.text}
            rows={5}
            onChange={(e) => {
              const newValue = e.target.value;
              setCurrentQuestion((prev) => ({
                ...prev,
                text: newValue,
                errors: { ...prev.errors, questionText: "" },
              }));
            }}
            // showLimit={true}
            // maxLimit={160}
            errorMessage={currentQuestion.errors?.questionText}
            counterContainerClass={styles.counter_container}
            errorClass={styles.error}
          />
        </div>

        <AnswerSection
          formData={formData}
          setFormData={setFormData}
          currentQuestion={currentQuestion}
          setCurrentQuestion={setCurrentQuestion}
          selectedAnswerType={selectedAnswerType}
          setSelectedAnswerType={setSelectedAnswerType}
          isEditing={isEditing}
         handleToggleEditState={handleToggleEditState}
        />
      </div>

      <QuestionSection
        formData={formData}
        setFormData={setFormData}
        currentQuestion={currentQuestion}
        setCurrentQuestion={setCurrentQuestion}
        validateQuestion={validateQuestion}
        isEditing={isEditing}
        handleToggleEditState={handleToggleEditState}
      />
    </div>
  );
};

export default MultipleCategorySection;
